// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import { Alert, AppIcon, Spinner, Button } from '@loa/ui-components';
import type { BrokerageDashboard, AdvisorMetrics, DashboardAlert } from '../types/admin';
import { useAdmin } from '../contexts/AdminContext';
import { useTags } from '../contexts/TagsContext';

const AlertCard: React.FC<{ alert: DashboardAlert }> = ({ alert }) => {
  const navigate = useNavigate();

  const getAlertColor = (type: string): 'red' | 'orange' | 'yellow' | 'blue' => {
    switch (type) {
      case 'EXPIRED_INVITATION':
      case 'PROVIDER_LOA_REJECTED':
      case 'INCOMPLETE_PROFILE':
      case 'INCOMPLETE_PROFILE_EXTENDED':
        return 'red';
      case 'STUCK_LOA':
      case 'PARTIALLY_SIGNED_EXTENDED':
        return 'orange';
      case 'ACTIVATION_PENDING':
        return 'yellow';
      case 'POLICY_HAS_OVERRIDE':
      case 'CLIENT_SIGNED_LOA':
      case 'CLIENT_ADDED_POLICY':
        return 'blue';
      case 'PROVIDER_DATA_AVAILABLE':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const getAlertIcon = (type: string): string => {
    switch (type) {
      case 'EXPIRED_INVITATION':
        return 'envelope outline';
      case 'STUCK_LOA':
        return 'file alternate outline';
      case 'PARTIALLY_SIGNED_EXTENDED':
        return 'clock outline';
      case 'ACTIVATION_PENDING':
        return 'clock outline';
      case 'INCOMPLETE_PROFILE_EXTENDED':
        return 'exclamation triangle';
      case 'POLICY_HAS_OVERRIDE':
        return 'exchange';
      case 'PROVIDER_LOA_REJECTED':
        return 'ban';
      case 'CLIENT_SIGNED_LOA':
        return 'check circle';
      case 'PROVIDER_DATA_AVAILABLE':
        return 'database';
      case 'CLIENT_ADDED_POLICY':
        return 'file alternate outline';
      case 'INCOMPLETE_PROFILE':
        return 'exclamation triangle';
      default:
        return 'info circle';
    }
  };

  const handleClick = () => {
    if (alert.profileId) {
      navigate(`/relationships/${alert.profileId}`);
    } else if (alert.advisorId) {
      navigate(`/admin/advisors`);
    }
  };

  const alertColorBg: Record<string, string> = {
    red: '#db2828',
    orange: '#f2711c',
    yellow: '#fbbd08',
    blue: '#2185d0',
  };
  const color = getAlertColor(alert.type);

  return (
    <button
      type="button"
      className="fin-card"
      onClick={handleClick}
      style={{ cursor: 'pointer', marginBottom: '0.5em', textAlign: 'left', background: 'transparent', border: 0, width: '100%' }}
    >
      <div className="fin-card__content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '0.5em' }}>
          <span
            style={{
              background: alertColorBg[color] || '#2185d0',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '3px',
              fontSize: '0.8em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3em',
            }}
          >
            <AppIcon name={getAlertIcon(alert.type) as 'clock outline'} />
            {alert.type.replaceAll('_', ' ')}
          </span>
        </div>
        <div style={{ marginTop: '0.5rem' }}>{alert.message}</div>
        {alert.daysStale && (
          <div style={{ marginTop: '0.5rem', color: '#999', fontSize: '0.85em' }}>
            {alert.daysStale} days since last activity
          </div>
        )}
      </div>
    </button>
  );
};

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { featureEnabled: tagsFeatureEnabled } = useTags();
  const [dashboard, setDashboard] = useState<BrokerageDashboard | null>(null);
  const [advisorMetrics, setAdvisorMetrics] = useState<AdvisorMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardData, metricsData] = await Promise.all([
        adminApi.getDashboardSummary(),
        adminApi.getAdvisorMetrics(0, 10),
      ]);

      setDashboard(dashboardData);
      setAdvisorMetrics(metricsData.content);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadDashboard();
    } else if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [adminLoading, isAdmin, loadDashboard, navigate]);

  if (adminLoading || loading) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert variant="error" header="Access Denied">
          <p>You do not have permission to access the admin dashboard.</p>
        </Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert variant="error" header="Error">
          <p>{error}</p>
          <Button variant="filled" onClick={loadDashboard}>Retry</Button>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '2em 3em' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5em' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
          <AppIcon name="dashboard" />
          Admin Dashboard
        </h1>
        <p style={{ color: '#666', margin: '0.25em 0 0 0' }}>Brokerage overview and management</p>
      </div>

      {/* Summary Statistics */}
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0', marginBottom: '1.5em' }}>
        <h3 style={{ margin: '0 0 1em 0' }}>Brokerage Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1em' }}>
          {[
            { value: dashboard?.totalAdvisors || 0, label: 'Advisors' },
            { value: dashboard?.activeAdvisors || 0, label: 'Active' },
            { value: dashboard?.totalProfiles || 0, label: 'Relationships' },
            { value: dashboard?.totalClients || 0, label: 'Clients' },
            { value: dashboard?.pendingLOAs || 0, label: 'Pending LOAs' },
            { value: dashboard?.completedLOAsThisMonth || 0, label: 'LOAs This Month' },
          ].map(({ value, label }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333' }}>{value}</div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Two-column grid: Alerts + Advisor Performance */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5em', marginBottom: '1.5em' }}>
        {/* Alerts Section */}
        <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5em', margin: '0 0 1em 0' }}>
            <AppIcon name="bell" />
            Alerts
            {dashboard?.alerts && dashboard.alerts.length > 0 && (
              <span style={{ background: '#db2828', color: 'white', borderRadius: '50%', padding: '2px 7px', fontSize: '0.75em', marginLeft: '0.25em' }}>
                {dashboard.alerts.length}
              </span>
            )}
          </h3>
          {dashboard?.alerts && dashboard.alerts.length > 0 ? (
            <div>
              {dashboard.alerts.slice(0, 5).map((alert) => (
                <AlertCard
                  key={`${alert.type}-${alert.profileId ?? ''}-${alert.advisorId ?? ''}-${alert.createdAt}`}
                  alert={alert}
                />
              ))}
            </div>
          ) : (
            <Alert variant="success" header="All Clear!">
              <p>No alerts at this time.</p>
            </Alert>
          )}
          {dashboard?.alerts && dashboard.alerts.length > 5 && (
            <Button variant="outlined" fluid style={{ marginTop: '1rem' }}>
              View All {dashboard.alerts.length} Alerts
            </Button>
          )}
        </div>

        {/* Advisor Performance Section */}
        <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5em', margin: 0 }}>
              <AppIcon name="users" />
              Advisor Performance
            </h3>
            <Button variant="filled" size="sm" onClick={() => navigate('/admin/advisors')}>
              Manage Advisors
            </Button>
          </div>
          {advisorMetrics.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75em' }}>
              {advisorMetrics.slice(0, 5).map((advisor) => (
                <div
                  key={advisor.advisorId}
                  style={{ padding: '0.75em', background: '#f9f9f9', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      {advisor.advisorName}
                      {advisor.isAdmin && (
                        <span style={{ background: '#2185d0', color: 'white', fontSize: '0.75em', padding: '1px 5px', borderRadius: '3px' }}>
                          Admin
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#666', fontSize: '0.9em' }}>{advisor.advisorEmail}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '1em', textAlign: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{advisor.profileCount}</div>
                      <div style={{ fontSize: '0.75em', color: '#666' }}>Relationships</div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                        {Math.round(advisor.loaCompletionRate * 100)}%
                      </div>
                      <div style={{ fontSize: '0.75em', color: '#666' }}>LOA Rate</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert variant="info">
              <p>No advisor data available.</p>
            </Alert>
          )}
        </div>
      </div>

      {/* Divider */}
      <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '0 0 1.5em 0' }} />

      {/* Quick Actions */}
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
        <h3 style={{ margin: '0 0 1em 0' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '0.75em', flexWrap: 'wrap' }}>
          <Button variant="filled" onClick={() => navigate('/admin/advisors')}>
            <AppIcon name="users" /> Manage Advisors
          </Button>
          <Button variant="outlined" onClick={() => navigate('/admin/assignments')}>
            <AppIcon name="exchange" /> Assignment History
          </Button>
          {tagsFeatureEnabled && (
            <Button variant="outlined" onClick={() => navigate('/admin/tags')}>
              <AppIcon name="edit" /> Manage Tags
            </Button>
          )}
          <Button variant="outlined" onClick={loadDashboard}>
            <AppIcon name="refresh" /> Refresh Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
